import axiosInstance from '../utils/axios';

class FinancialCalculationService {
  // Helper function to safely format numbers
  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    return parseFloat(value).toLocaleString();
  }

  // Calculate revenue from all sources
  calculateTotalRevenue(data) {
    const schoolFeesRevenue = data.studentFinance?.totalFees || 0;
    const canteenRevenue = data.canteenFees?.totalRevenue || 0;
    const salesRevenue = data.sales?.totalRevenue || 0;
    
    return {
      schoolFees: schoolFeesRevenue,
      canteenFees: canteenRevenue,
      sales: salesRevenue,
      otherIncome: 0,
      total: schoolFeesRevenue + canteenRevenue + salesRevenue
    };
  }

  // Calculate expenses from all sources
  calculateTotalExpenses(data) {
    const salaryExpenses = data.salaries?.totalSalaries || 0;
    const operationalExpenses = data.expenditure?.totalExpenses || 0;
    
    // Categorize expenses
    const utilities = this.categorizeExpenses(data.expenditure?.expenses || [], 'utility');
    const maintenance = this.categorizeExpenses(data.expenditure?.expenses || [], 'maintenance');
    const supplies = this.categorizeExpenses(data.expenditure?.expenses || [], 'supply');
    const otherExpenses = operationalExpenses - utilities - maintenance - supplies;

    return {
      salaries: salaryExpenses,
      utilities: utilities,
      maintenance: maintenance,
      supplies: supplies,
      otherExpenses: otherExpenses,
      total: salaryExpenses + operationalExpenses
    };
  }

  // Categorize expenses by keyword
  categorizeExpenses(expenses, keyword) {
    return expenses
      .filter(expense => 
        expense.category && 
        expense.category.toLowerCase().includes(keyword.toLowerCase())
      )
      .reduce((total, expense) => total + (parseFloat(expense.amount) || 0), 0);
  }

  // Calculate current assets
  calculateCurrentAssets(data) {
    const cash = 50000; // Base cash amount
    const salesRevenue = data.sales?.totalRevenue || 0;
    const schoolFeesCollected = data.studentFinance?.paidFees || 0;
    const canteenFeesCollected = data.canteenFees?.totalRevenue || 0;
    const totalExpenses = data.expenditure?.totalExpenses || 0;
    
    const accountsReceivable = (data.studentFinance?.outstandingFees || 0) + 
                              (data.canteenFees?.outstandingAmount || 0);
    
    const inventory = (data.sales?.totalRevenue || 0) * 0.3; // 30% of sales as inventory
    const prepaidExpenses = this.categorizeExpenses(data.expenditure?.expenses || [], 'prepaid');

    return {
      cash: cash + salesRevenue + schoolFeesCollected + canteenFeesCollected - totalExpenses,
      accountsReceivable: accountsReceivable,
      inventory: inventory,
      prepaidExpenses: prepaidExpenses,
      total: cash + salesRevenue + schoolFeesCollected + canteenFeesCollected - totalExpenses + 
             accountsReceivable + inventory + prepaidExpenses
    };
  }

  // Calculate fixed assets
  calculateFixedAssets(data) {
    const assets = data.assets?.assets || [];
    
    const equipment = this.calculateAssetValue(assets, 'Equipment');
    const furniture = this.calculateAssetValue(assets, 'Furniture');
    const vehicles = this.calculateAssetValue(assets, 'Vehicle');
    const buildings = this.calculateAssetValue(assets, 'Building');
    const technology = this.calculateAssetValue(assets, 'Technology');
    const books = this.calculateAssetValue(assets, 'Books');
    
    const totalAssetValue = equipment + furniture + vehicles + buildings + technology + books;
    const accumulatedDepreciation = totalAssetValue * 0.2; // 20% depreciation

    return {
      equipment: equipment,
      furniture: furniture,
      vehicles: vehicles,
      buildings: buildings,
      technology: technology,
      books: books,
      accumulatedDepreciation: -accumulatedDepreciation,
      total: totalAssetValue - accumulatedDepreciation
    };
  }

  // Calculate asset value by type
  calculateAssetValue(assets, assetType) {
    return assets
      .filter(asset => asset.assetType === assetType && asset.status === 'Active')
      .reduce((total, asset) => {
        const value = parseFloat(asset.currentValue) || parseFloat(asset.purchaseCost) || 0;
        return total + value;
      }, 0);
  }

  // Calculate current liabilities
  calculateCurrentLiabilities(data) {
    const accountsPayable = (data.expenditure?.expenses || [])
      .filter(expense => expense.paymentStatus === 'pending' || expense.paymentStatus === 'unpaid')
      .reduce((total, expense) => total + (parseFloat(expense.amount) || 0), 0);
    
    const accruedSalaries = (data.salaries?.totalSalaries || 0) * 0.1; // 10% accrued
    const accruedUtilities = this.categorizeExpenses(data.expenditure?.expenses || [], 'utility') * 0.2;
    const shortTermDebt = 20000; // Fixed amount

    return {
      accountsPayable: accountsPayable,
      accruedSalaries: accruedSalaries,
      accruedUtilities: accruedUtilities,
      shortTermDebt: shortTermDebt,
      total: accountsPayable + accruedSalaries + accruedUtilities + shortTermDebt
    };
  }

  // Calculate long-term liabilities
  calculateLongTermLiabilities(data) {
    const longTermDebt = 100000; // Fixed amount
    const deferredRevenue = (data.studentFinance?.paidFees || 0) - (data.studentFinance?.totalFees || 0);
    
    return {
      longTermDebt: longTermDebt,
      deferredRevenue: Math.max(0, deferredRevenue),
      total: longTermDebt + Math.max(0, deferredRevenue)
    };
  }

  // Calculate equity
  calculateEquity(data, netIncome) {
    const capital = 200000; // Fixed amount
    const previousRetainedEarnings = 50000; // Base amount
    const retainedEarnings = previousRetainedEarnings + netIncome;

    return {
      capital: capital,
      retainedEarnings: retainedEarnings,
      currentYearEarnings: netIncome,
      total: capital + retainedEarnings
    };
  }

  // Generate comprehensive balance sheet
  generateBalanceSheet(data) {
    const currentAssets = this.calculateCurrentAssets(data);
    const fixedAssets = this.calculateFixedAssets(data);
    const currentLiabilities = this.calculateCurrentLiabilities(data);
    const longTermLiabilities = this.calculateLongTermLiabilities(data);
    
    const revenue = this.calculateTotalRevenue(data);
    const expenses = this.calculateTotalExpenses(data);
    const netIncome = revenue.total - expenses.total;
    
    const equity = this.calculateEquity(data, netIncome);

    return {
      assets: {
        currentAssets: currentAssets,
        fixedAssets: fixedAssets,
        totalAssets: currentAssets.total + fixedAssets.total
      },
      liabilities: {
        currentLiabilities: currentLiabilities,
        longTermLiabilities: longTermLiabilities,
        totalLiabilities: currentLiabilities.total + longTermLiabilities.total
      },
      equity: equity,
      totalLiabilitiesAndEquity: currentLiabilities.total + longTermLiabilities.total + equity.total
    };
  }

  // Generate income statement
  generateIncomeStatement(data) {
    const revenue = this.calculateTotalRevenue(data);
    const expenses = this.calculateTotalExpenses(data);
    const netIncome = revenue.total - expenses.total;

    return {
      revenue: revenue,
      expenses: expenses,
      grossProfit: revenue.total - expenses.salaries,
      netIncome: netIncome
    };
  }

  // Generate trial balance
  generateTrialBalance(data) {
    const balanceSheet = this.generateBalanceSheet(data);
    const incomeStatement = this.generateIncomeStatement(data);

    return [
      { account: 'Cash', debit: balanceSheet.assets.currentAssets.cash, credit: 0 },
      { account: 'Accounts Receivable', debit: balanceSheet.assets.currentAssets.accountsReceivable, credit: 0 },
      { account: 'Inventory', debit: balanceSheet.assets.currentAssets.inventory, credit: 0 },
      { account: 'Equipment', debit: balanceSheet.assets.fixedAssets.equipment, credit: 0 },
      { account: 'Accounts Payable', debit: 0, credit: balanceSheet.liabilities.currentLiabilities.accountsPayable },
      { account: 'School Fees Revenue', debit: 0, credit: incomeStatement.revenue.schoolFees },
      { account: 'Canteen Fees Revenue', debit: 0, credit: incomeStatement.revenue.canteenFees },
      { account: 'Sales Revenue', debit: 0, credit: incomeStatement.revenue.sales },
      { account: 'Salary Expense', debit: incomeStatement.expenses.salaries, credit: 0 },
      { account: 'Utilities Expense', debit: incomeStatement.expenses.utilities, credit: 0 },
      { account: 'Maintenance Expense', debit: incomeStatement.expenses.maintenance, credit: 0 },
      { account: 'Supplies Expense', debit: incomeStatement.expenses.supplies, credit: 0 },
      { account: 'Other Expenses', debit: incomeStatement.expenses.otherExpenses, credit: 0 }
    ];
  }

  // Generate cash flow statement
  generateCashFlowStatement(data) {
    const incomeStatement = this.generateIncomeStatement(data);
    const balanceSheet = this.generateBalanceSheet(data);
    
    const netIncome = incomeStatement.netIncome;
    const depreciation = 5000; // Fixed depreciation amount
    
    // Calculate working capital changes
    const accountsReceivableChange = -balanceSheet.assets.currentAssets.accountsReceivable * 0.1;
    const inventoryChange = -2000; // Inventory reduction
    const accountsPayableChange = balanceSheet.liabilities.currentLiabilities.accountsPayable * 0.1;
    
    // Calculate investing activities
    const equipmentPurchase = this.categorizeExpenses(data.expenditure?.expenses || [], 'equipment') * -1;
    const buildingImprovements = this.categorizeExpenses(data.expenditure?.expenses || [], 'building') * -1;
    
    // Calculate financing activities
    const loanProceeds = 20000;
    const loanPayments = -5000;
    const capitalContributions = 10000;
    
    const netCashChange = netIncome + depreciation + accountsReceivableChange + 
                         inventoryChange + accountsPayableChange + equipmentPurchase + 
                         buildingImprovements + loanProceeds + loanPayments + capitalContributions;

    return {
      operatingActivities: {
        netIncome: netIncome,
        depreciation: depreciation,
        accountsReceivableChange: accountsReceivableChange,
        inventoryChange: inventoryChange,
        accountsPayableChange: accountsPayableChange
      },
      investingActivities: {
        equipmentPurchase: equipmentPurchase,
        buildingImprovements: buildingImprovements
      },
      financingActivities: {
        loanProceeds: loanProceeds,
        loanPayments: loanPayments,
        capitalContributions: capitalContributions
      },
      netCashChange: netCashChange
    };
  }

  // Generate payment accounts
  generatePaymentAccounts(data) {
    const schoolFees = data.studentFinance || {};
    const canteenFees = data.canteenFees || {};
    const salaries = data.salaries || {};
    const expenses = data.expenditure || {};

    return {
      schoolFees: {
        total: schoolFees.totalFees || 0,
        paid: schoolFees.paidFees || 0,
        outstanding: schoolFees.outstandingFees || 0,
        discount: 0
      },
      canteenFees: {
        total: canteenFees.totalRevenue || 0,
        paid: canteenFees.totalRevenue || 0,
        outstanding: canteenFees.outstandingAmount || 0,
        discount: 0
      },
      salaries: {
        total: salaries.totalSalaries || 0,
        paid: salaries.totalSalaries || 0,
        outstanding: 0,
        discount: 0
      },
      expenses: {
        total: expenses.totalExpenses || 0,
        paid: expenses.totalExpenses || 0,
        outstanding: 0,
        discount: 0
      }
    };
  }

  // Generate general ledger
  generateGeneralLedger(financialData, startDate, endDate, selectedAccount = 'all') {
    const { studentFinance, canteenFees, salaries, expenditure, sales, assets } = financialData;
    
    // Define chart of accounts based on real data
    const accounts = [
      {
        accountCode: '1000',
        accountName: 'Cash and Cash Equivalents',
        accountType: 'Asset',
        description: 'Cash on hand and in bank accounts',
        balance: 50000, // Base cash amount
        transactions: []
      },
      {
        accountCode: '1100',
        accountName: 'Accounts Receivable',
        accountType: 'Asset',
        description: 'Outstanding school fees',
        balance: studentFinance?.outstandingFees || 0,
        transactions: this.generateStudentFeeTransactions(studentFinance, startDate, endDate)
      },
      {
        accountCode: '1500',
        accountName: 'Fixed Assets',
        accountType: 'Asset',
        description: 'School equipment and facilities',
        balance: assets?.totalValue || 0,
        transactions: this.generateAssetTransactions(assets, startDate, endDate)
      },
      {
        accountCode: '2000',
        accountName: 'Accounts Payable',
        accountType: 'Liability',
        description: 'Outstanding bills and expenses',
        balance: expenditure?.totalAmount || 0,
        transactions: this.generateExpenseTransactions(expenditure, startDate, endDate)
      },
      {
        accountCode: '3000',
        accountName: 'Owner\'s Equity',
        accountType: 'Equity',
        description: 'School capital and retained earnings',
        balance: 100000, // Base equity
        transactions: []
      },
      {
        accountCode: '4000',
        accountName: 'School Fees Revenue',
        accountType: 'Revenue',
        description: 'Revenue from school fees',
        balance: studentFinance?.totalFees || 0,
        transactions: this.generateRevenueTransactions(studentFinance, startDate, endDate)
      },
      {
        accountCode: '4100',
        accountName: 'Canteen Revenue',
        accountType: 'Revenue',
        description: 'Revenue from canteen sales',
        balance: canteenFees?.totalRevenue || 0,
        transactions: this.generateCanteenTransactions(canteenFees, startDate, endDate)
      },
      {
        accountCode: '5000',
        accountName: 'Salaries and Wages',
        accountType: 'Expense',
        description: 'Staff salaries and benefits',
        balance: salaries?.totalSalaries || 0,
        transactions: this.generateSalaryTransactions(salaries, startDate, endDate)
      },
      {
        accountCode: '5100',
        accountName: 'Operating Expenses',
        accountType: 'Expense',
        description: 'General operating expenses',
        balance: expenditure?.totalAmount || 0,
        transactions: this.generateOperatingExpenseTransactions(expenditure, startDate, endDate)
      }
    ];

    // Filter by selected account if not 'all'
    const filteredAccounts = selectedAccount === 'all' 
      ? accounts 
      : accounts.filter(account => account.accountCode === selectedAccount);

    return {
      accounts: filteredAccounts,
      totalTransactions: filteredAccounts.reduce((total, account) => 
        total + (account.transactions?.length || 0), 0),
      period: { startDate, endDate }
    };
  }

  // Helper methods to generate transactions for each account type
  generateStudentFeeTransactions(studentFinance, startDate, endDate) {
    if (!studentFinance?.students) return [];
    
    return studentFinance.students.map(student => ({
      date: new Date().toISOString().split('T')[0],
      description: `School fees - ${student.name || 'Student'}`,
      reference: `SF-${student.id || 'N/A'}`,
      debit: student.balance || 0,
      credit: 0,
      balance: student.balance || 0
    }));
  }

  generateAssetTransactions(assets, startDate, endDate) {
    if (!assets?.assets) return [];
    
    return assets.assets.map(asset => ({
      date: asset.purchaseDate || new Date().toISOString().split('T')[0],
      description: `Asset purchase - ${asset.assetName}`,
      reference: `AST-${asset.assetNumber}`,
      debit: asset.purchaseCost || 0,
      credit: 0,
      balance: asset.purchaseCost || 0
    }));
  }

  generateExpenseTransactions(expenditure, startDate, endDate) {
    if (!expenditure?.expenses) return [];
    
    return expenditure.expenses.map(expense => ({
      date: expense.date || new Date().toISOString().split('T')[0],
      description: expense.purpose || 'Operating expense',
      reference: `EXP-${expense.id || 'N/A'}`,
      debit: 0,
      credit: parseFloat(expense.amountspent) || 0,
      balance: parseFloat(expense.amountspent) || 0
    }));
  }

  generateRevenueTransactions(studentFinance, startDate, endDate) {
    if (!studentFinance?.students) return [];
    
    return studentFinance.students.map(student => ({
      date: new Date().toISOString().split('T')[0],
      description: `School fees revenue - ${student.name || 'Student'}`,
      reference: `REV-${student.id || 'N/A'}`,
      debit: 0,
      credit: student.totalFees || 0,
      balance: student.totalFees || 0
    }));
  }

  generateCanteenTransactions(canteenFees, startDate, endDate) {
    if (!canteenFees?.transactions) return [];
    
    return canteenFees.transactions.map(transaction => ({
      date: transaction.date || new Date().toISOString().split('T')[0],
      description: `Canteen sale - ${transaction.description || 'Food item'}`,
      reference: `CAN-${transaction.id || 'N/A'}`,
      debit: 0,
      credit: transaction.amount || 0,
      balance: transaction.amount || 0
    }));
  }

  generateSalaryTransactions(salaries, startDate, endDate) {
    if (!salaries?.teachers) return [];
    
    return salaries.teachers.map(teacher => ({
      date: new Date().toISOString().split('T')[0],
      description: `Salary - ${teacher.name || 'Teacher'}`,
      reference: `SAL-${teacher.id || 'N/A'}`,
      debit: teacher.salary || 0,
      credit: 0,
      balance: teacher.salary || 0
    }));
  }

  generateOperatingExpenseTransactions(expenditure, startDate, endDate) {
    if (!expenditure?.expenses) return [];
    
    return expenditure.expenses.map(expense => ({
      date: expense.date || new Date().toISOString().split('T')[0],
      description: expense.purpose || 'Operating expense',
      reference: `OP-${expense.id || 'N/A'}`,
      debit: parseFloat(expense.amountspent) || 0,
      credit: 0,
      balance: parseFloat(expense.amountspent) || 0
    }));
  }
}

export default new FinancialCalculationService(); 